import { useState, useCallback, useEffect, useRef } from 'react';
import { AssistantMessage, ASSISTANT_SESSION_TIMEOUT_MS } from '@recruitai/shared';
import {
  createAssistantSession,
  sendAssistantMessage,
  getAssistantMessages,
  listAssistantSessions,
} from '../lib/assistant-api';

export interface AssistantMessageUI {
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  isError?: boolean;
}

const SUGGESTIONS = [
  'Quantas vagas abertas temos?',
  'Quem são os melhores candidatos?',
  'Resuma o pipeline de recrutamento',
];

const WELCOME_MESSAGE = 'Olá! Sou o assistente IA da TalentumIQ. Posso ajudar você a consultar informações sobre vagas, candidatos, entrevistas e métricas da sua organização. Como posso ajudar?';

export function useAssistant() {
  const [messages, setMessages] = useState<AssistantMessageUI[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [suggestions] = useState<string[]>(SUGGESTIONS);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [slowLoading, setSlowLoading] = useState(false);

  /**
   * Carrega sessão ativa ou cria uma nova
   */
  const loadSession = useCallback(async (existingSessionId?: string) => {
    try {
      setError(null);

      if (existingSessionId) {
        setSessionId(existingSessionId);
        const response = await getAssistantMessages(existingSessionId);
        setMessages(response.messages.map(toUI));
        return;
      }

      // Tentar encontrar sessão ativa
      const sessionsResponse = await listAssistantSessions();
      const activeSessions = sessionsResponse.sessions.filter((s) => {
        if (s.status !== 'active') return false;
        // Verificar se não expirou por inatividade
        const lastActivity = new Date(s.lastActivityAt).getTime();
        return Date.now() - lastActivity <= ASSISTANT_SESSION_TIMEOUT_MS;
      });

      if (activeSessions.length > 0) {
        const latest = activeSessions[0];
        setSessionId(latest.sessionId);
        const response = await getAssistantMessages(latest.sessionId);
        setMessages(response.messages.map(toUI));
      } else {
        // Criar nova sessão
        const response = await createAssistantSession();
        setSessionId(response.sessionId);
        setMessages([
          {
            messageId: 'welcome',
            role: 'assistant',
            content: response.welcomeMessage || WELCOME_MESSAGE,
            createdAt: new Date().toISOString(),
          },
        ]);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar sessão.');
    }
  }, []);

  /**
   * Envia uma mensagem ao assistente
   */
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      setError(null);
      setSlowLoading(false);

      // Criar sessão se não existir
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        try {
          const response = await createAssistantSession();
          currentSessionId = response.sessionId;
          setSessionId(currentSessionId);
        } catch (err: any) {
          setError('Erro ao criar sessão. Tente novamente.');
          return;
        }
      }

      // Adicionar mensagem do usuário imediatamente
      const userMsg: AssistantMessageUI = {
        messageId: `temp-${Date.now()}`,
        role: 'user',
        content: content.trim(),
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      // Timer para loading lento (20s)
      loadingTimerRef.current = setTimeout(() => {
        setSlowLoading(true);
      }, 20000);

      try {
        const response = await sendAssistantMessage(currentSessionId, content.trim());
        setMessages((prev) => [
          ...prev,
          {
            messageId: response.messageId || `resp-${Date.now()}`,
            role: 'assistant',
            content: response.content,
            createdAt: new Date().toISOString(),
          },
        ]);
      } catch (err: any) {
        // Sessão expirada (410) — criar nova sessão e reenviar
        if (err.statusCode === 410 || err.code === 'SESSION_EXPIRED') {
          try {
            const newSession = await createAssistantSession();
            setSessionId(newSession.sessionId);
            setMessages([
              {
                messageId: 'welcome',
                role: 'assistant',
                content: newSession.welcomeMessage || WELCOME_MESSAGE,
                createdAt: new Date().toISOString(),
              },
              userMsg,
            ]);
            const retryResponse = await sendAssistantMessage(newSession.sessionId, content.trim());
            setMessages((prev) => [
              ...prev,
              {
                messageId: retryResponse.messageId || `resp-${Date.now()}`,
                role: 'assistant',
                content: retryResponse.content,
                createdAt: new Date().toISOString(),
              },
            ]);
          } catch (retryErr: any) {
            setError(retryErr.message || 'Erro ao recriar sessão. Tente novamente.');
            setMessages((prev) => [
              ...prev,
              {
                messageId: `error-${Date.now()}`,
                role: 'assistant',
                content: retryErr.message || 'Erro ao recriar sessão.',
                createdAt: new Date().toISOString(),
                isError: true,
              },
            ]);
          }
        } else {
          const errorMessage =
            err.code === 'TIMEOUT'
              ? 'A IA demorou muito para responder. Tente novamente.'
              : err.message || 'Erro ao enviar mensagem. Tente novamente.';
          setError(errorMessage);
          setMessages((prev) => [
            ...prev,
            {
              messageId: `error-${Date.now()}`,
              role: 'assistant',
              content: errorMessage,
              createdAt: new Date().toISOString(),
              isError: true,
            },
          ]);
        }
      } finally {
        setIsLoading(false);
        setSlowLoading(false);
        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current);
          loadingTimerRef.current = null;
        }
      }
    },
    [sessionId, isLoading]
  );

  // Carregar sessão ao montar
  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    };
  }, []);

  return {
    messages,
    isLoading,
    slowLoading,
    error,
    sessionId,
    sendMessage,
    loadSession,
    suggestions,
  };
}

function toUI(msg: AssistantMessage): AssistantMessageUI {
  return {
    messageId: msg.messageId,
    role: msg.role,
    content: msg.content,
    createdAt: msg.createdAt,
  };
}
